import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import RackDetailModal from "../../components/RackDetailModal";
import RackDimensionHeatmap from "../../components/RackDimensionHeatmap";
import { Search } from "lucide-react";
import {
    FlowCard,
    StockAccuracyCard,
    PalletUtilizationCard,
    UserLoginCard,
    OccupancyCard,
    Panel,
    PanelHeader,
    formatNumber
} from "../../components/DashboardKpiCards";

import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip
} from "recharts";

import { fetchMasterWarehouses } from "../raking/warehouseSlice";

import {
    fetchRacks,
    fetchRecommendation,
    fetchRackDetail,
    clearDetail,
    fetchDashboard,
    fetchPalletCapacity,
    fetchStockAccuracy,
    searchRacks
} from "./dashboardSlice";

// ambil nilai pertama yang terdefinisi (untuk toleransi nama field API)
const pick = (...vals) => {
    for (const v of vals) {
        if (v !== undefined && v !== null && v !== "") return v;
    }
    return null;
};

export default function Dashboard() {

    const dispatch = useDispatch();

    const {
        summary,
        lastUpdated,
        racks,
        recommendation,
        rackDetail,
        palletCapacity,
        stockAccuracy,
        loading,
        searchRacks: rackSearchResult
    } = useSelector((state) => state.dashboard);

    const [selectedRack, setSelectedRack] = useState(null);

    /* ===== USER LOGIN & LAST UPDATE (WIB / Asia/Jakarta) ===== */
    const authUser = useSelector((state) => state.auth?.user);
    const userName = authUser?.name || authUser?.username || authUser?.email || "-";

    // total untuk legend grafik kapasitas
    const chartTotals = useMemo(() => {
        return (palletCapacity?.racks_locations || []).reduce(
            (acc, r) => ({
                capacity: acc.capacity + Number(r.total_capacity || 0),
                used: acc.used + Number(r.used_capacity || 0),
                available: acc.available + Number(r.available_capacity || 0)
            }),
            { capacity: 0, used: 0, available: 0 }
        );
    }, [palletCapacity]);

    // "Diperbarui x menit lalu" — diperbarui tiap 30 detik
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);

    const updatedAgo = useMemo(() => {
        if (!lastUpdated) return "-";
        const diffMs = nowTick - new Date(lastUpdated).getTime();
        if (Number.isNaN(diffMs)) return "-";
        const min = Math.floor(Math.max(diffMs, 0) / 60000);
        if (min < 1) return "baru saja";
        if (min < 60) return `${min} menit lalu`;
        const jam = Math.floor(min / 60);
        if (jam < 24) return `${jam} jam lalu`;
        return `${Math.floor(jam / 24)} hari lalu`;
    }, [lastUpdated, nowTick]);

    const lastUpdatedText = useMemo(() => {
        if (!lastUpdated) return "-";
        const d = new Date(lastUpdated);
        if (Number.isNaN(d.getTime())) return "-";
        const tanggal = d.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            timeZone: "Asia/Jakarta"
        });
        const jam = d.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
            timeZone: "Asia/Jakarta"
        }).replace(/\./g, ":");
        return `${tanggal}, ${jam} WIB`;
    }, [lastUpdated]);

    const masterWarehouses = useSelector(
        (state) => state.masterWarehouses
    );

    const [showDueDateModal, setShowDueDateModal] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("daily");
    const [showDimensionModal, setShowDimensionModal] = useState(false);
    const [selectedRackDimension, setSelectedRackDimension] = useState(null);
    const warehouses = masterWarehouses?.data || [];
    const [searchRack, setSearchRack] = useState("");

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [page, setPage] = useState(1);

    const itemsPerPage = 10;

    // FILTER DATA
    const filteredData = useMemo(() => {

        return (
            palletCapacity?.bin_locations?.filter((item) =>
                item.kode_palet
                    ?.toLowerCase()
                    .includes(search.toLowerCase())
            ) || []
        );

    }, [palletCapacity, search]);

    // PAGINATION
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {

        dispatch(fetchRacks(selectedWarehouse));

        dispatch(fetchRecommendation(selectedWarehouse));

        dispatch(fetchPalletCapacity({
            warehouse_id: selectedWarehouse,
            search,
            page,
        }));

        dispatch(fetchMasterWarehouses());

    }, [dispatch, selectedWarehouse, search, currentPage, page]);

    useEffect(() => {

        dispatch(fetchStockAccuracy(selectedWarehouse));

    }, [dispatch, selectedWarehouse]);

    // summary + inbound/outbound ikut filter warehouse & periode
    useEffect(() => {

        dispatch(fetchDashboard({
            warehouse_id: selectedWarehouse,
            period: selectedPeriod
        }));

    }, [dispatch, selectedWarehouse, selectedPeriod]);

    const handleClickRack = (rack) => {

        setSelectedRack(rack);

        dispatch(
            fetchRackDetail({
                id: rack.racking_id,
                code: rack.level_code
            })
        );
    };

    const closeModal = () => {

        setSelectedRack(null);

        dispatch(clearDetail());
    };

    /*const openDimensionModal = (rack) => {

        setSelectedRackDimension(rack);

        dispatch(fetchRackDetail({
            id: rack.id,
            code: rack.code
        }));

        setShowDimensionModal(true);
    };*/

    const openDimensionModal = async (rack) => {

        setSelectedRackDimension(rack);

        const result = await dispatch(fetchRackDetail({
            id: rack.id,
            code: rack.code
        }));

        //console.log("RESULT", result.payload);

        if (result.payload?.length > 0) {
            setShowDimensionModal(true);
        }
    };

    const closeDimensionModal = () => {

        setSelectedRackDimension(null);

        setShowDimensionModal(false);
    };

    const capacitySummary = palletCapacity?.summary || {};

    // "Semua Warehouse" -> tampilkan nama warehouse di grafik & tabel
    const isAllWarehouse = !selectedWarehouse;

    const rackChartData = useMemo(() => {
        return (palletCapacity?.racks_locations || []).map((r) => ({
            ...r,
            label: isAllWarehouse && r.warehouse_name
                ? `${r.racks_name} (${r.warehouse_name})`
                : r.racks_name
        }));
    }, [palletCapacity, isAllWarehouse]);

    /* ===== KPI: INBOUND / OUTBOUND (dari endpoint /dashboard) ===== */
    const PERIOD_OPTIONS = [
        { value: "daily", label: "Harian", compare: "vs kemarin" },
        { value: "weekly", label: "Mingguan", compare: "vs minggu lalu" },
        { value: "monthly", label: "Bulanan", compare: "vs bulan lalu" }
    ];
    const periodCompareLabel =
        PERIOD_OPTIONS.find((p) => p.value === selectedPeriod)?.compare || "";

    const inboundTotal = pick(summary?.inbound?.total, 0);
    const inboundDiff = pick(summary?.inbound?.diff);

    const outboundTotal = pick(summary?.outbound?.total, 0);
    const outboundDiff = pick(summary?.outbound?.diff);

    /* ===== KPI: STOCK ACCURACY ===== */
    const accuracyValue = pick(summary?.stock_accuracy, stockAccuracy?.accuracy);
    const accuracyChange = pick(summary?.stock_accuracy_change);

    /* ===== KPI: PALLET UTILIZATION (dari endpoint /dashboard) =====
       Di Rak   = palet master (bins) yang sedang menempati rak warehouse terpilih
       WH Lain  = palet master yang ada di rak warehouse lain
       Idle     = palet yang pernah dipakai tapi saat ini tidak di rak
       Palet master yang belum pernah dipakai dipisah (tidak masuk utilisasi) */
    const activePallets = Number(summary?.pallet_active || 0);
    const otherWhPallets = Number(summary?.pallet_other_wh || 0);
    const idlePallets = Number(summary?.pallet_idle || 0);
    const palletUtilPercent = pick(summary?.pallet_util_percent);
    const neverUsedPallets = Number(summary?.pallet_never_used || 0);
    const masterPallets = Number(summary?.pallet_master_total || 0);

    /* OCCUPANCY = slot rak terisi / total slot (rows x columns) -> lihat OccupancyCard */

    useEffect(() => {

        const timer = setTimeout(() => {

            if (searchRack.trim()) {

                dispatch(
                    searchRacks({
                        warehouse_id: selectedWarehouse,
                        keyword: searchRack
                    })
                );

            }

        }, 500);

        return () => clearTimeout(timer);

    }, [searchRack, selectedWarehouse]);

    const rackData =
        searchRack.trim().length > 0
            ? rackSearchResult
            : racks;

    const warehouseLabel = isAllWarehouse
        ? "Semua warehouse"
        : warehouses.find((w) => String(w.id) === String(selectedWarehouse))?.name || "Warehouse";

    const thClass = "px-4 py-2.5 text-left text-xs font-medium text-muted bg-surface border-b border-line whitespace-nowrap";
    const tdClass = "px-4 py-2.5 border-b border-line text-sm whitespace-nowrap";

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ===== BAR FILTER ===== */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">

                <div>
                    <h2 className="text-2xl font-semibold text-body tracking-tight">
                        {warehouseLabel}
                    </h2>
                    <p className="text-sm text-muted mt-1">
                        Ringkasan stok, rak, dan pergerakan barang
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">

                    <label className="flex flex-col gap-1">
                        <span className="text-xs text-muted">Warehouse</span>
                        <select
                            value={selectedWarehouse}
                            onChange={(e) => setSelectedWarehouse(e.target.value)}
                            className="h-10 min-w-[220px] bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0"
                        >
                            <option value="">Semua warehouse</option>
                            {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {w.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted">Periode inbound / outbound</span>
                        <div
                            className="h-10 inline-flex bg-white border border-line rounded-md p-1"
                            role="radiogroup"
                            aria-label="Periode"
                        >
                            {PERIOD_OPTIONS.map((p) => {
                                const active = selectedPeriod === p.value;
                                return (
                                    <button
                                        key={p.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => setSelectedPeriod(p.value)}
                                        className={`px-3.5 text-sm rounded transition-colors ${
                                            active
                                                ? "bg-ink text-white font-semibold"
                                                : "text-muted hover:text-body"
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                </div>
            </div>

            {/* ===== KPI BARIS 1 ===== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

                <FlowCard
                    type="inbound"
                    value={inboundTotal}
                    trend={inboundDiff}
                    trendSuffix={periodCompareLabel}
                    loading={loading?.dashboard}
                />

                <FlowCard
                    type="outbound"
                    value={outboundTotal}
                    trend={outboundDiff}
                    trendSuffix={periodCompareLabel}
                    loading={loading?.dashboard}
                />

                <StockAccuracyCard
                    accuracy={accuracyValue}
                    change={accuracyChange}
                    matched={stockAccuracy?.matched}
                    total={stockAccuracy?.total}
                    loading={loading?.stockAccuracy}
                />

                <UserLoginCard
                    name={userName}
                    lastUpdated={lastUpdatedText}
                    loading={loading?.dashboard}
                    dueCount={Number(capacitySummary?.total_due_date || 0)}
                    onDueClick={() => setShowDueDateModal(true)}
                />

            </div>

            {/* ===== KPI BARIS 2 ===== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                <OccupancyCard
                    percent={capacitySummary.used_percent}
                    used={capacitySummary.used_qty}
                    total={capacitySummary.total_capacity}
                    pallets={summary.total_palet}
                    totalRack={summary.total_rack}
                    loading={loading?.palletCapacity}
                />

                <PalletUtilizationCard
                    active={activePallets}
                    other={otherWhPallets}
                    idle={idlePallets}
                    percent={palletUtilPercent}
                    neverUsed={neverUsedPallets}
                    masterTotal={masterPallets}
                    loading={loading?.dashboard}
                />

            </div>

            {/* ===== GRAFIK KAPASITAS RACKING ===== */}
            <Panel>
                <PanelHeader
                    title="Kapasitas racking"
                    meta={`${formatNumber(palletCapacity?.racks_locations?.length || 0)} rak, kapasitas dihitung dalam slot (baris × kolom)`}
                />

                <div className="px-3 pt-4 pb-2">
                    <ResponsiveContainer width="100%" height={isAllWarehouse ? 380 : 300}>
                        <BarChart
                            data={rackChartData}
                            barGap={2}
                            barCategoryGap="22%"
                            margin={{ top: 4, right: 8, left: isAllWarehouse ? 90 : 0, bottom: 0 }}
                        >

                            <CartesianGrid vertical={false} stroke="#E8EEF7" />

                            <XAxis
                                dataKey="label"
                                interval={0}
                                angle={isAllWarehouse ? -35 : 0}
                                textAnchor={isAllWarehouse ? "end" : "middle"}
                                height={isAllWarehouse ? 120 : 30}
                                tick={{ fontSize: 11, fill: "#5F6B7D" }}
                                tickLine={false}
                                axisLine={{ stroke: "#DCE4EE" }}
                            />

                            <YAxis
                                tick={{ fontSize: 11, fill: "#5F6B7D" }}
                                tickLine={false}
                                axisLine={false}
                                width={40}
                            />

                            <Tooltip
                                cursor={{ fill: "#F2F5FA" }}
                                contentStyle={{
                                    borderRadius: 6,
                                    border: "1px solid #DCE4EE",
                                    fontSize: 12,
                                    boxShadow: "none"
                                }}
                            />

                            <Bar dataKey="total_capacity" name="Kapasitas" fill="#D3DEEC" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="used_capacity" name="Terisi" fill="#0A6CF0" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="available_capacity" name="Tersedia" fill="#7DB8FF" radius={[3, 3, 0, 0]} />

                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* LEGEND */}
                <div className="border-t border-line bg-surface/60 rounded-b-lg px-5 py-3 text-center">
                    <ul className="flex flex-wrap justify-center gap-x-8 gap-y-1 text-[15px] text-body">
                        {[
                            { label: "Kapasitas", value: chartTotals.capacity, color: "#D3DEEC" },
                            { label: "Terisi", value: chartTotals.used, color: "#0A6CF0" },
                            { label: "Tersedia", value: chartTotals.available, color: "#7DB8FF" }
                        ].map((it) => (
                            <li key={it.label} className="flex items-center gap-2">
                                <span
                                    className="w-3 h-3 rounded-full shrink-0"
                                    style={{ background: it.color }}
                                />
                                <span>
                                    {it.label} • <span className="font-semibold tabular-nums">{formatNumber(it.value)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-sm text-muted mt-1">
                        Total slot terisi: <span className="tabular-nums">{formatNumber(chartTotals.used)} / {formatNumber(chartTotals.capacity)}</span> slot
                        {" • "}
                        Diperbarui {updatedAgo}
                    </p>
                </div>
            </Panel>

            {/* ===== LOKASI PALET ===== */}
            <Panel>
                <PanelHeader
                    title="Lokasi palet"
                    meta={`${formatNumber(filteredData.length)} baris stok di rak`}
                >
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                        <input
                            type="search"
                            placeholder="Cari kode palet"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="h-9 w-64 max-w-full pl-9 pr-3 text-sm border border-line rounded-md focus:border-ink focus:ring-0"
                        />
                    </div>
                </PanelHeader>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                {isAllWarehouse && <th className={thClass}>Warehouse</th>}
                                <th className={thClass}>Rak</th>
                                <th className={thClass}>Level</th>
                                <th className={thClass}>Palet</th>
                                <th className={thClass}>SKU</th>
                                <th className={thClass}>Nama item</th>
                                <th className={thClass}>Batch</th>
                                <th className={`${thClass} text-right`}>Qty</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((item, i) => (
                                    <tr key={i} className="hover:bg-surface/60">
                                        {isAllWarehouse && (
                                            <td className={`${tdClass} text-muted`}>
                                                {item.warehouse_name || "-"}
                                            </td>
                                        )}
                                        <td className={`${tdClass} font-semibold`}>{item.racks_name}</td>
                                        <td className={`${tdClass} tabular-nums`}>{item.level_code}</td>
                                        <td className={tdClass}>
                                            {item.kode_palet ? (
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-ink-100 text-ink text-xs font-semibold">
                                                    {item.kode_palet}
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td className={`${tdClass} tabular-nums`}>{item.sku}</td>
                                        <td className={`${tdClass} max-w-[320px] truncate`} title={item.description}>
                                            {item.description}
                                        </td>
                                        <td className={`${tdClass} tabular-nums`}>{item.batch_number}</td>
                                        <td className={`${tdClass} text-right font-semibold tabular-nums`}>
                                            {formatNumber(item.qty)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={isAllWarehouse ? 8 : 7}
                                        className="text-center px-4 py-10 text-sm text-muted"
                                    >
                                        {search
                                            ? `Tidak ada palet dengan kode "${search}". Periksa lagi kodenya.`
                                            : "Belum ada stok di rak untuk warehouse ini."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <span className="text-muted">
                        Halaman {currentPage} dari {totalPages || 1}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((prev) => prev - 1)}
                            className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                            Sebelumnya
                        </button>
                        <button
                            type="button"
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                            Berikutnya
                        </button>
                    </div>
                </div>
            </Panel>

            {/* ===== LIST RACK ===== */}
            <Panel>
                <PanelHeader
                    title="Daftar rak"
                    meta={`${formatNumber(rackData?.length || 0)} rak`}
                >
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                        <input
                            type="search"
                            placeholder="Cari rak, kode item, nama item, atau batch"
                            value={searchRack}
                            onChange={(e) => setSearchRack(e.target.value)}
                            className="h-9 w-80 max-w-full pl-9 pr-3 text-sm border border-line rounded-md focus:border-ink focus:ring-0"
                        />
                    </div>
                </PanelHeader>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                {isAllWarehouse && <th className={thClass}>Warehouse</th>}
                                <th className={thClass}>Nama rak</th>
                                <th className={`${thClass} text-right`}>Baris</th>
                                <th className={`${thClass} text-right`}>Kolom</th>
                                <th className={`${thClass} text-right`}>Total slot</th>
                                <th className={`${thClass} text-right`}>Terisi</th>
                                <th className={`${thClass} w-[220px]`}>Pemakaian</th>
                                <th className={`${thClass} text-right`}></th>
                            </tr>
                        </thead>

                        <tbody>
                            {rackData?.length > 0 ? (
                                rackData.map((rack) => {
                                    const pct = rack.available_capacity > 0
                                        ? Math.round((rack.used_capacity / rack.available_capacity) * 100)
                                        : 0;
                                    const barColor = pct >= 100
                                        ? "bg-red-500"
                                        : pct >= 70
                                            ? "bg-tag"
                                            : "bg-ink";

                                    return (
                                        <tr key={rack.id} className="hover:bg-surface/60">
                                            {isAllWarehouse && (
                                                <td className={`${tdClass} text-muted`}>
                                                    {rack.warehouse_name || "-"}
                                                </td>
                                            )}
                                            <td className={`${tdClass} font-semibold`}>{rack.racks_name}</td>
                                            <td className={`${tdClass} text-right tabular-nums`}>{rack.total_rows}</td>
                                            <td className={`${tdClass} text-right tabular-nums`}>{rack.total_columns}</td>
                                            <td className={`${tdClass} text-right tabular-nums`}>{formatNumber(rack.available_capacity)}</td>
                                            <td className={`${tdClass} text-right tabular-nums`}>{formatNumber(rack.used_capacity)}</td>
                                            <td className={tdClass}>
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1 h-2 rounded-full bg-[#E8EEF7] overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${barColor}`}
                                                            style={{ width: `${Math.min(pct, 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="w-10 text-right text-xs font-semibold tabular-nums">
                                                        {pct}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={`${tdClass} text-right`}>
                                                <button
                                                    type="button"
                                                    onClick={() => openDimensionModal(rack)}
                                                    className="h-8 px-3 text-sm font-medium text-ink border border-line rounded-md hover:border-ink hover:bg-ink-100 whitespace-nowrap"
                                                >
                                                    Lihat denah
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={isAllWarehouse ? 8 : 7}
                                        className="text-center px-4 py-10 text-sm text-muted"
                                    >
                                        {searchRack
                                            ? `Tidak ada rak yang cocok dengan "${searchRack}".`
                                            : "Belum ada rak untuk warehouse ini."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Panel>


            {/* DETAIL MODAL */}
            <RackDetailModal
                rack={selectedRack}
                data={rackDetail}
                onClose={closeModal}
            />

            {/* DIMENSION MODAL */}
            {showDimensionModal && selectedRackDimension && (

                <RackDimensionHeatmap
                    rack={selectedRackDimension}
                    details={rackDetail}
                    onClose={closeDimensionModal}
                />

            )}

            {/* DUE DATE MODAL */}
            {showDueDateModal && (

                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">

                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">

                        {/* HEADER */}
                        <div className="flex items-center justify-between px-6 py-4 border-b">

                            <div>
                                <h2 className="text-xl font-bold">
                                    Batch jatuh tempo dalam 12 bulan
                                </h2>

                                <p className="text-sm text-gray-500">
                                    Total Item: {palletCapacity?.daftar_duedate?.length || 0}
                                </p>
                            </div>

                            <button
                                onClick={() => setShowDueDateModal(false)}
                                className="text-gray-500 hover:text-black text-xl"
                            >
                                ✕
                            </button>

                        </div>

                        {/* CONTENT */}
                        <div className="overflow-auto max-h-[75vh]">

                            <table className="w-full border-collapse">

                                <thead className="sticky top-0 bg-gray-100 z-10">

                                    <tr>

                                        <th className="border p-3 text-left">
                                            SKU
                                        </th>

                                        <th className="border p-3 text-left">
                                            Item Name
                                        </th>

                                        <th className="border p-3 text-left">
                                            Batch
                                        </th>

                                        <th className="border p-3 text-left">
                                            Due Date
                                        </th>

                                        <th className="border p-3 text-center">
                                            Remaining
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {palletCapacity?.daftar_duedate?.length > 0 ? (

                                        palletCapacity.daftar_duedate.map((item, index) => {

                                            const dueDate = new Date(item.due_date);
                                            const today = new Date();

                                            const diffTime =
                                                dueDate.getTime() - today.getTime();

                                            const diffDays = Math.ceil(
                                                diffTime / (1000 * 60 * 60 * 24)
                                            );

                                            return (

                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50"
                                                >

                                                    <td className="border p-3">
                                                        {item.sku}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.product_name}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.batch_no}
                                                    </td>

                                                    <td className="border p-3">
                                                        {item.due_date}
                                                    </td>

                                                    <td className="border p-3 text-center">

                                                        <span
                                                            className={`
                                                    px-2 py-1 rounded-lg text-xs font-semibold
                                                    ${diffDays <= 30
                                                                    ? "bg-red-100 text-red-700"
                                                                    : diffDays <= 90
                                                                        ? "bg-yellow-100 text-yellow-700"
                                                                        : "bg-green-100 text-green-700"
                                                                }
                                                `}
                                                        >

                                                            {diffDays > 0
                                                                ? `${diffDays} hari`
                                                                : "Expired"}

                                                        </span>

                                                    </td>

                                                </tr>

                                            );
                                        })

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan="5"
                                                className="text-center p-6 text-gray-500"
                                            >
                                                Tidak ada data due date
                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
}